import {
	ClientCarFuelEnum,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/i;

export const getCarFormSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};
	const positiveIntegerMessages = {
		positiveMessage: t(Resources.Portfolio.AddCar.Validations.positiveInteger),
		maxMessage,
	};
	const notInFutureMessage = t(
		Resources.Portfolio.AddCar.Validations.notInFuture,
	);

	return z.object({
		carBrand: trimmedNullable(
			z
				.string()
				.min(1, t(Resources.Portfolio.AddShared.Validations.required))
				.max(
					ValidationLimits.TextLength.Name,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.Name,
					}),
				),
		),
		carModel: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
		),
		propertyType: z.nativeEnum(ClientPropertyVehicleTypeEnum),
		carFuel: z.nativeEnum(ClientCarFuelEnum).nullable(),
		carLicensePlate: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.Code,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.Code,
				}),
			),
		),
		carVin: trimmedNullable(
			z
				.string()
				.refine(
					(val) => val === "" || VIN_PATTERN.test(val),
					t(Resources.Portfolio.AddCar.Validations.vinFormat),
				),
		),
		value: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		carPurchaseDate: optionalDateAsString(DateFormat.Iso),
		carRegistration: optionalDateAsString(DateFormat.Iso, notInFutureMessage),
		carManufactureMonthYear: optionalDateAsString(
			DateFormat.MonthYear,
			notInFutureMessage,
		),
		carPower: optionalPositiveInteger(
			ValidationLimits.Amount.MaxPower,
			positiveIntegerMessages,
		),
		carVolume: optionalPositiveInteger(
			ValidationLimits.Amount.MaxVolume,
			positiveIntegerMessages,
		),
		description: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.Description,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.Description,
				}),
			),
		),
		forInvestments: z.boolean(),
	});
};

export type CarFormInput = z.input<ReturnType<typeof getCarFormSchema>>;
export type CarFormOutput = z.output<ReturnType<typeof getCarFormSchema>>;
